<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.academic.setup.program.grade.grade') }}
      </h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('school.academic.setup.program.grade.grade'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-12 col-md-10">
            <ts-button color="info" outline @click="$router.push({ name: 'grade-create' })">
              {{ $t('add_new') }}
            </ts-button>
            <ts-button color="info" class="ml-2" outline @click="defineNextGrade">
              {{ $t('school.academic.setup.program.grade.next_grade') }}
            </ts-button>
          </div>
          <div class="col-sm-12 col-md-2 d-flex justify-content-end">
            <ts-input v-model="search" :placeholder="$t('search')" />
            <ts-button outline class="ml-2" v-tooltip="$t('filter')" @click.prevent="visible = true">
                <i class="fa fa-filter"></i>
              </ts-button>
          </div>
          <a-drawer :title="$t('invoice.filter')" placement="right" :closable="false" :visible="visible"
              @close="() => (visible = false)" :zIndex="1020" width="300px">
              <div style="font-size: 12px">
                <div class="row">
                  <div class="form-group">
                    <label class="control-label">
                      {{
                        $t(
                          'school.academic.setup.program.grade.scoring_type'
                        )
                      }}</label>
                    <div>
                      <a-select v-model="model.score_type" :options="scoreTypes" :placeholder="$t('all')" @change="scoreTypeOptions"
                        style="width: 250px" showSearch mode="multiple" :filterOption="(input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                          " :maxTagCount="1"></a-select>
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="control-label">
                      {{
                        $t(
                          'school.academic.setup.program.grade.monthlyScore'
                        )
                      }}</label>
                    <div>
                      <a-select v-model="model.monthly_score_sc_bas_id" :options="monthlyScores" :placeholder="$t('all')" @change="monthlyScoreOptions"
                        style="width: 250px" showSearch mode="multiple" :filterOption="(input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                          " :maxTagCount="1"></a-select>
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="control-label">
                      {{
                        $t(
                          'school.academic.setup.program.grade.semesterScore'
                        )
                      }}</label>
                    <div>
                      <a-select v-model="model.semester_score_sc_bas_id" :options="semesterScore" :placeholder="$t('all')" @change="semesterScoreOptions"
                        style="width: 250px" showSearch mode="multiple" :filterOption="(input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                          " :maxTagCount="1"></a-select>
                    </div>
                  </div>
                </div>
              </div>
            </a-drawer>
        </div>
      </ts-card-wrapper>
      <div class="scrollable">
        <ts-table :columns="columns" :records="resources" :loading="loading" :rowGroup="{
          field: 'level.level_with_sub'
        }">
          <template v-slot="{ record }">
            <td class="kh-font ts-whitespace-no-wrap">
              {{
                (record.grade_name_native
                  ? record.grade_name_native + ' | '
                  : '') + record.grade_name_foreign
              }}
            </td>
            <td class="ts-whitespace-no-wrap">{{ record.next_grade_name }}</td>
            <td class="text-center ts-whitespace-no-wrap">
              {{ record.grade_order }}
            </td>
            <td class="ts-whitespace-no-wrap">{{ record.grade_abbr }}</td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.score_type == 1
                ? 'Numbering'
                : record.score_type == 2
                  ? 'Grading'
                  : 'Rating'
              }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.monthly_score_sc_bas_id == 1 ? 'Item Base' : 'Subject Base'
              }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.semester_score_sc_bas_id == 1
                ? 'Item Base'
                : 'Subject Base'
              }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.level
                ? record.level.sub_program
                  ? record.level.sub_program.main_program
                    ? record.level.sub_program.main_program.acp_name_foreign
                      ? record.level.sub_program.main_program.acp_name_foreign
                      : ''
                    : ''
                  : ''
                : ''
              }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.level
                ? record.level.sub_program
                  ? record.level.sub_program.acps_name_foreign
                    ? record.level.sub_program.acps_name_foreign
                    : ''
                  : ''
                : ''
              }}
            </td>
            <td class="text-center">
              <a href="#" v-tooltip="$t('edit')" class="mr-2 text-primary" @click.prevent="edit(record)">
                <i class="fas fa-edit"></i>
              </a>
              <a href="#" v-tooltip="$t('delete')" class="text-danger" @click.prevent="destroy(record)"
                :disabled="record._deleting">
                <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                <i class="fas fa-trash" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-between p-15">
        <h1 style="margin-top: 11px" class="ts-text-gray-500">
          <b><i><span class="text-danger">* </span>
              {{ $t('school.academic.setup.program.grade.note') }} :
            </i></b>
          {{
            $t(
              'school.academic.setup.program.grade.only_user_at_head_office_can_modify_or_delete'
            )
          }}
        </h1>
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import NextGrade from './next-grade'

export default {
  name: 'grade',
  data() {
    return {
      loading: false,
      visible: false,
      model: {
        score_type: undefined,
        monthly_score_sc_bas_id: undefined,
        semester_score_sc_bas_id: undefined
      }
    }
  },
  computed: {
    ...mapState('grade', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.grade.search
      },
      set(newValue) {
        this.$store.commit('grade/SET_SEARCH', newValue)
        this.$store.commit('grade/RESET_CURRENT_PAGE')
      }
    },
    scoreTypes () {
      return [
        { value: '1', label: 'Numbering' },
        { value: '2', label: 'Grading' },
        { value: '3', label: 'Rating' }
      ]
    },
    monthlyScores () {
      return [
        { value: '1', label: 'Item Base' },
        { value: '2', label: 'Subject Base' },
      ]
    },
    semesterScore () {
      return [
        { value: '1', label: 'Item Base' },
        { value: '2', label: 'Subject Base' },
      ]
    },
    columns() {
      return [
        {
          name: this.$t('school.academic.setup.program.grade.gradeName')
        },
        {
          name: this.$t('school.academic.setup.program.grade.next_grade')
        },
        {
          name: this.$t('school.academic.setup.program.grade.grade_order'),
          style: 'width:7%; text-align:center'
        },
        {
          name: this.$t('school.academic.setup.program.grade.abbreviation'),
          style: 'width:12%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.scoring_type'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.monthlyScore'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.semesterScore'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.mainProgram'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.subProgram'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('grade/fetch', attributes)
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    defineNextGrade() {
      this.$modal.show(
        NextGrade,
        {},
        {
          height: 'auto',
          width: '30%',
          scrollable: true,
          clickToClose: false
        }
      )
    },
    edit(record) {
      this.$store.commit('grade/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'grade-edit',
        params: { id: record.grade_id }
      })
    },
    scoreTypeOptions(value) {
      this.$store.commit('grade/SET_SCORE_TYPE', value)
      this.fetchData()
    },
    monthlyScoreOptions(value) {
      this.$store.commit('grade/SET_MONTHLY_SCORE', value)
      this.fetchData()
    },
    semesterScoreOptions(value) {
      this.$store.commit('grade/SET_SEMESTER_SCORE', value)
      this.fetchData()
    },
    async destroy(record) {
      if (await this.$confirmModal.danger('Are you sure to delete ?')) {
        record._deleting = true
        this.$store
          .dispatch('grade/destroy', record.grade_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500),
    score_type: debounce(function () {
      this.fetchData()
    }, 500),
    monthly_score_sc_bas_id: debounce(function () {
      this.fetchData()
    }, 500),
    semester_score_sc_bas_id: debounce(function () {
      this.fetchData()
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'grade-create') {
      this.$store.commit('grade/RESET_STATE')
    }
    next()
  }
}
</script>
<style>
.scrollable {
  width: 100%;
  overflow-x: scroll;
}
</style>
